import { RefObject, useEffect, useMemo, useState } from 'react'

type Props = {
  w: number
  h: number
  containerRef: RefObject<HTMLDivElement | null>
  items: any[]
  min?: number
  scaleFactor: number
}

export const useOverflow = ({
  w,
  h,
  containerRef,
  items,
  min = 1,
  scaleFactor,
}: Props) => {
  const [visibleCount, setVisibleCount] = useState(0)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const checkOverflow = () => {
      let cumulativeWidth = 0
      let rowHeight = 0
      let visibleItemsCount = 0
      let lastTopOffset = 0

      // Safety check for container children
      const children = Array.from(container.children)
      if (!children.length) {
        setVisibleCount(0)
        return
      }

      children.forEach((child, index) => {
        const element = child as HTMLElement
        const childStyle = getComputedStyle(element)

        // Safely parse margin values with fallbacks to 0
        const marginLeft = parseInt(childStyle.marginLeft) || 0
        const marginRight = parseInt(childStyle.marginRight) || 0
        const marginTop = parseInt(childStyle.marginTop) || 0
        const marginBottom = parseInt(childStyle.marginBottom) || 0

        const childWidth = element.offsetWidth + marginLeft + marginRight
        const childHeight = element.offsetHeight + marginTop + marginBottom

        if (index === 0 || element.offsetTop === lastTopOffset) {
          // Item is in the same row
          cumulativeWidth += childWidth
          rowHeight = Math.max(rowHeight, childHeight)
        } else {
          // Item has wrapped to a new row
          cumulativeWidth = childWidth
          rowHeight +=
            lastTopOffset === 0 ? childHeight : childHeight + marginTop
        }

        lastTopOffset = element.offsetTop

        // Account for container padding/border in the calculation
        const containerPaddingBottom =
          parseInt(getComputedStyle(container).paddingBottom) || 0
        const availableHeight =
          container.offsetHeight - 8 * scaleFactor - containerPaddingBottom

        // Check if adding this item would overflow the container
        if (
          cumulativeWidth <= container.offsetWidth &&
          rowHeight <= availableHeight
        ) {
          visibleItemsCount++
        }
      })

      setVisibleCount(Math.max(0, visibleItemsCount))
    }

    checkOverflow()
    // Adding items.length ensures the effect reruns when the number of items changes
  }, [w, h, scaleFactor, items.length, containerRef])

  const finalVisibleCount = useMemo(() => {
    return visibleCount < min ? 0 : visibleCount
  }, [visibleCount, min])

  return { visibleCount: finalVisibleCount }
}
